import type { ReactNode } from 'react';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import { TYPING_DEBOUNCE_MS } from '../../constants';
import NumberField from './number-field';
import type { BoxModelValues } from './use-box-model-style';

interface FieldConfig {
  slot: 0 | 1 | 2 | 3;
  icon: string;
  ariaLabel: string;
}

interface BoxModelControlProps {
  sectionLabel: string;
  expanded: boolean;
  onToggleExpand: () => void;
  toggleIcon: string;
  toggleLabel: string;
  values: BoxModelValues;
  onChangeSlot: (slot: 0 | 1 | 2 | 3, value: number | undefined) => void;
  /** rendering order: entries 0/1 are the always-visible top row, 2/3 are the row revealed below
   * via SlideDownCard - not necessarily CSS shorthand order (e.g. corner-radius reads visually as
   * top-left/top-right/bottom-left/bottom-right, but the CSS order is TL/TR/BR/BL) */
  fields: [FieldConfig, FieldConfig, FieldConfig, FieldConfig];
  renderCollapsed: () => ReactNode;
}

// icon-only toggle that expands a box-model section into its 4 independent side/corner fields
function ExpandToggleButton({
  icon,
  expanded,
  onClick,
  label,
}: {
  icon: string;
  expanded: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <Button
      clear
      className="ai-center jc-center flex-shrink-0"
      width="32px"
      height="32px"
      padding="0"
      borderRadius="100px"
      design="LocalBttnSecHalfTransp"
      backgroundColor={expanded ? 'var(--global-cont-2)' : undefined}
      onClick={onClick}
      aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
      title={expanded ? `Collapse ${label}` : `Expand ${label}`}
    >
      <Icon name={icon} size={12} />
    </Button>
  );
}

// shared shape for the Corner radius/Padding/Margin sections: a collapsed view (given via
// renderCollapsed) that expands into a 2x2 grid of independent side/corner fields
export default function BoxModelControl({
  sectionLabel,
  expanded,
  onToggleExpand,
  toggleIcon,
  toggleLabel,
  values,
  onChangeSlot,
  fields,
  renderCollapsed,
}: BoxModelControlProps) {
  const [topLeft, topRight, bottomLeft, bottomRight] = fields;
  return (
    <Container className="flex-column">
      <Container className="flex gap-8 ai-flex-end">
        <Container className="flex-1">
          {expanded ? (
            <Container className="flex-column gap-2">
              <Text tag="label" design="GlobalIcoText3" className="L2 padding-l-16">
                {sectionLabel}
              </Text>
              <Container className="flex gap-12">
                <NumberField
                  ariaLabel={topLeft.ariaLabel}
                  icon={topLeft.icon}
                  allowNegative={false}
                  min={0}
                  max={100}
                  value={values[topLeft.slot]}
                  onChange={(v) => onChangeSlot(topLeft.slot, v)}
                  debounceTime={TYPING_DEBOUNCE_MS}
                />
                <NumberField
                  ariaLabel={topRight.ariaLabel}
                  icon={topRight.icon}
                  allowNegative={false}
                  min={0}
                  max={100}
                  value={values[topRight.slot]}
                  onChange={(v) => onChangeSlot(topRight.slot, v)}
                  debounceTime={TYPING_DEBOUNCE_MS}
                />
              </Container>
            </Container>
          ) : (
            renderCollapsed()
          )}
        </Container>
        <ExpandToggleButton icon={toggleIcon} expanded={expanded} onClick={onToggleExpand} label={toggleLabel} />
      </Container>
      <SlideDownCard open={expanded} autoGrow animateOnInitialLoad={false}>
        <Container className="flex gap-8" padding="12px 0 0 0">
          <Container className="flex gap-12 flex-1">
            <NumberField
              ariaLabel={bottomLeft.ariaLabel}
              icon={bottomLeft.icon}
              allowNegative={false}
              min={0}
              max={100}
              value={values[bottomLeft.slot]}
              onChange={(v) => onChangeSlot(bottomLeft.slot, v)}
              debounceTime={TYPING_DEBOUNCE_MS}
            />
            <NumberField
              ariaLabel={bottomRight.ariaLabel}
              icon={bottomRight.icon}
              allowNegative={false}
              min={0}
              max={100}
              value={values[bottomRight.slot]}
              onChange={(v) => onChangeSlot(bottomRight.slot, v)}
              debounceTime={TYPING_DEBOUNCE_MS}
            />
          </Container>
          <Container width="32px" className="flex-shrink-0" />
        </Container>
      </SlideDownCard>
    </Container>
  );
}
